import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import * as CANNON from 'cannon-es';
import TWEEN from '@tweenjs/tween.js';
import { animateSpaceObjects } from '@modules/animation/animateSpaceObjects.js';
import { audioManager } from '@modules/audio/AudioManager.js';
import { navigationManager } from '@modules/navigation/NavigationManager.js';
import { notificationManager, showNotification } from '@modules/ui';
import { menuManager } from '@modules/menu/MenuManager.js';
import TouchControls from '@modules/interaction/TouchControls.js';
import KeyboardNavigation from '@modules/interaction/KeyboardNavigation.js';
// eslint-disable-next-line no-unused-vars
const _notificationManager = notificationManager;

const CONFIG = {
  camera: { fov: 75, near: 0.1, far: 10000, position: { x: 0, y: 80, z: 200 } },
  renderer: {
    antialias: true,
    alpha: true,
    shadowMap: { enabled: true, type: THREE.PCFSoftShadowMap },
    toneMapping: THREE.ACESFilmicToneMapping,
    toneMappingExposure: 1.0
  },
  scene: { background: 0x1a1a2e },
  lights: {
    ambient: { color: 0xffffff, intensity: 0.2 },
    directional: { color: 0xffffff, intensity: 1, position: { x: 0, y: 0, z: 1 } }
  },
  performance: { stats: import.meta.env.DEV, fpsLimit: 60 }
};

const appState = {
  currentFocusTarget: null,
  currentPlanetIndex: -1,
  targetPlanetToFollow: null,
  animationLoopActive: false,
  previousCameraPosition: null
};

class App {
  constructor() {
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.world = null;
    this.sun = null;

    this.audioManager = audioManager;
    this.navigationManager = navigationManager;
    this.menuManager = menuManager;
    this.touchControls = null;
    this.keyboardNavigation = null;

    this.isInitialized = false;
    this.isAnimating = false;
    this.animationFrameId = null;
    this.stats = null;
    this.clock = new THREE.Clock();
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    this.planets = [];
    this.moons = [];
    this.spaceObjects = [];
    this.satellites = [];
    this.asteroidBelt = null;
    this.ufo = null;
    this.comet = null;
    this.rocket = null;
    this.spaceStation = null;
    this.infoCard = null;

    this.init = this.init.bind(this);
    this.animate = this.animate.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.cleanup = this.cleanup.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  async init() {
    if (this.isInitialized) {
      console.warn('App is already initialized');
      return;
    }

    try {
      await this._initManagers();
      this._setupScene();
      this._setupCamera();
      this._setupRenderer();
      this.world = new CANNON.World({ gravity: new CANNON.Vec3(0, 0, 0) });
      this._setupControls();
      await this._setupSceneObjects();
      console.log('Initializing NavigationManager with scene dependencies...');
      await navigationManager.init({
        camera: this.camera,
        controls: this.controls,
        sceneObjects: {
          planets: this.planets,
          moons: this.moons,
          sun: this.sun,
          comet: this.comet,
          ufo: this.ufo,
          spaceStation: this.spaceStation,
          rocket: this.rocket,
          asteroidBelt: this.asteroidBelt,
          satellites: this.satellites
        }
      });
      console.log('NavigationManager initialized successfully with dependencies');
      this.touchControls = new TouchControls(this.renderer.domElement, this.controls);
      this.keyboardNavigation = new KeyboardNavigation();
      this._setupEventListeners();
      this.infoCard = this._createInfoCardSystem();
      await this._loadAssets();
      await this._initMenu();

      // Tambahkan item menu ke MenuManager setelah objek scene siap
      this._setupMenuItems();

      this.startAnimationLoop();
      this.isInitialized = true;
      console.log('App initialized successfully');
      this._hideLoadingScreen();
    } catch (error) {
      console.error('Failed to initialize App:', error);
      showNotification('Failed to initialize application.', 'error');
      throw error;
    }
  }

  async _initManagers() {
    const managers = [
      { name: 'Audio', instance: this.audioManager },
      { name: 'Menu', instance: this.menuManager }
    ];

    for (const { name, instance } of managers) {
      try {
        if (instance && typeof instance.init === 'function') {
          console.log(`Initializing ${name}Manager...`);
          await instance.init();
          console.log(`${name}Manager initialized successfully`);
        }
      } catch (error) {
        console.error(`Error initializing ${name}Manager:`, error);
        throw error;
      }
    }
  }

  _setupScene() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(CONFIG.scene.background);
    const ambientLight = new THREE.AmbientLight(
      CONFIG.lights.ambient.color,
      CONFIG.lights.ambient.intensity
    );
    this.scene.add(ambientLight);
  }

  _setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      CONFIG.camera.fov,
      window.innerWidth / window.innerHeight,
      CONFIG.camera.near,
      CONFIG.camera.far
    );
    this.camera.position.set(
      CONFIG.camera.position.x,
      CONFIG.camera.position.y,
      CONFIG.camera.position.z
    );
  }

  _setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: CONFIG.renderer.antialias,
      alpha: CONFIG.renderer.alpha,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = CONFIG.renderer.shadowMap.enabled;
    this.renderer.shadowMap.type = CONFIG.renderer.shadowMap.type;
    this.renderer.toneMapping = CONFIG.renderer.toneMapping;
    this.renderer.toneMappingExposure = CONFIG.renderer.toneMappingExposure;

    const container = document.getElementById('container') || document.body;
    container.appendChild(this.renderer.domElement);

    if (CONFIG.performance.stats) {
      this.stats = new Stats();
      document.body.appendChild(this.stats.dom);
    }
  }

  _setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = false;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 500;
    this.controls.maxPolarAngle = Math.PI / 2;
  }

  _setupEventListeners() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.handleResize(), 100);
    }, false);

    window.addEventListener('beforeunload', this.cleanup.bind(this));
    this.renderer.domElement.addEventListener('click', this.handleClick, { passive: false });

    window.addEventListener('error', (event) => {
      console.error('Unhandled error:', event.error);
      showNotification(`Error: ${event.message || 'Unknown error'}`, 'error', 10000);
    });

    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      showNotification(`Error: ${event.reason || 'Unknown error'}`, 'error', 10000);
      event.preventDefault();
    });
  }

  handleClick(event) {
    event.preventDefault();
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const objectsToCheck = [
      ...(this.planets?.map(p => p.group) || []),
      ...(this.moons?.map(m => m.group) || []),
      this.comet?.group,
      this.spaceStation?.group,
      ...(this.satellites?.map(s => s.group) || []),
      this.ufo,
      this.rocket,
      this.asteroidBelt,
      this.sun
    ].filter(obj => obj && obj.visible);

    const intersects = this.raycaster.intersectObjects(objectsToCheck, true);
    if (intersects.length > 0) {
      const targetObject = intersects[0].object.parent || intersects[0].object;
      if (targetObject) {
        this.navigationManager.focusOnObject(targetObject);
      }
    }
  }

  async _setupSceneObjects() {
    this.sun = this._createSun();
    this.scene.add(this.sun);
    this.spaceObjects.push({ group: this.sun, name: 'Sun', description: 'The star at the center of our solar system' });

    this.planets = this._createPlanets();
    this.planets.forEach(planet => {
      if (planet && planet.group) {
        this.scene.add(planet.group);
        this.spaceObjects.push(planet);
      }
    });

    this.moons = [
      this._createMoon(this.planets[2], 1.5, 0xdddddd, 10, 0.03, 'Moon'),
      this._createMoon(this.planets[3], 0.5, 0xaaaaaa, 4, 0.03, 'Phobos'),
      this._createMoon(this.planets[3], 0.4, 0x999999, 6, 0.02, 'Deimos')
    ];
    this.moons.forEach(moon => {
      if (moon && moon.group) {
        this.spaceObjects.push(moon);
      }
    });

    this.asteroidBelt = this._createAsteroidBelt();
    this.scene.add(this.asteroidBelt);
    this.spaceObjects.push({ group: this.asteroidBelt, name: 'Asteroid Belt', description: 'A belt of rocky debris between Mars and Jupiter' });

    this.ufo = this._createUFO();
    this.scene.add(this.ufo);
    this.spaceObjects.push({ group: this.ufo, name: 'UFO', description: 'An unknown spacecraft observing the solar system' });

    this.satellites = [this._createSatellite(), this._createSatellite(), this._createSatellite()];
    this.satellites.forEach(satellite => {
      if (satellite && satellite.group) {
        this.scene.add(satellite.group);
        this.spaceObjects.push(satellite);
      }
    });

    this.comet = this._createComet();
    this.scene.add(this.comet.group);
    this.spaceObjects.push(this.comet);

    this.rocket = this._createRocket();
    this.scene.add(this.rocket);
    this.spaceObjects.push({ group: this.rocket, name: 'Rocket', description: 'A space rocket exploring the solar system' });
  }

  _createSun() {
    const sunGeometry = new THREE.SphereGeometry(10, 32, 32);
    const sunMaterial = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      emissive: 0xffffff,
      emissiveIntensity: 1.0,
      shininess: 0
    });
    const sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
    sunMesh.name = 'sun';
    sunMesh.castShadow = true;

    const sunLight = new THREE.PointLight(0xffffff, 3.5, 3000, 1.0);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(1024, 1024);
    sunLight.shadow.camera.near = 0.5;
    sunLight.shadow.camera.far = 500;
    sunMesh.add(sunLight);

    const glowGeometry = new THREE.SphereGeometry(15, 32, 32);
    const glowMaterial = new THREE.ShaderMaterial({
      uniforms: { intensity: { value: 0.9 } },
      vertexShader: `
        varying vec3 vNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float intensity;
        varying vec3 vNormal;
        void main() {
          float edge = 0.7 * abs(dot(vNormal, vec3(0.0, 0.0, 1.0)));
          edge = pow(edge, 3.0);
          vec3 glow = vec3(1.0, 1.0, 0.95) * intensity / (1.1 * edge + 0.1);
          gl_FragColor = vec4(glow, 1.0 - edge);
        }
      `,
      side: THREE.BackSide,
      blending: THREE.AdditiveBlending,
      transparent: true
    });
    const sunGlow = new THREE.Mesh(glowGeometry, glowMaterial);
    sunGlow.scale.set(1.5, 1.5, 1.5);
    sunMesh.add(sunGlow);

    return sunMesh;
  }

  _createPlanets() {
    return [
      this._createPlanet(1.5, 0x8a8a8a, new THREE.Vector3(120, 0, 0), 120, 0.0004, 0.01, 0, false, 0xffffff, null, 'Mercury', 'The smallest and innermost planet with extreme temperature variations. Distance from Sun: 0.39 AU (58 million km)'),
      this._createPlanet(3.7, 0xe6e6e6, new THREE.Vector3(180, 0, 0), 180, 0.0003, 0.005, 177.3, false, 0xffffff, null, 'Venus', 'Similar in size to Earth but with a toxic atmosphere of carbon dioxide. Distance from Sun: 0.72 AU (108 million km)'),
      this._createPlanet(3.9, 0x1a66ff, new THREE.Vector3(250, 0, 0), 250, 0.0002, 0.01, 23.5, false, 0xffffff, null, 'Earth', 'Our home planet, the only known place with life. Distance from Sun: 1 AU (150 million km)'),
      this._createPlanet(2.1, 0xc1440e, new THREE.Vector3(380, 0, 0), 380, 0.00015, 0.009, 25.2, false, 0xffffff, null, 'Mars', 'The Red Planet with the largest volcano in the solar system. Distance from Sun: 1.52 AU (228 million km)'),
      this._createPlanet(43, 0xb07f35, new THREE.Vector3(1300, 0, 0), 1300, 0.00008, 0.02, 3.1, false, 0xffffff, null, 'Jupiter', 'The largest planet with a Great Red Spot storm. Distance from Sun: 5.2 AU (778 million km)'),
      this._createPlanet(36, 0xe3dccb, new THREE.Vector3(2400, 0, 0), 2400, 0.00005, 0.015, 26.7, true, 0xd4c7a9, null, 'Saturn', 'Famous for its beautiful ring system. Distance from Sun: 9.58 AU (1.4 billion km)'),
      this._createPlanet(15.5, 0xd1e7e7, new THREE.Vector3(4800, 0, 0), 4800, 0.00003, 0.01, 97.8, true, 0xffffff, null, 'Uranus', 'Rotates on its side with a tilted magnetic field. Distance from Sun: 19.2 AU (2.9 billion km)'),
      this._createPlanet(15, 0x3b5bdb, new THREE.Vector3(7500, 0, 0), 7500, 0.00002, 0.008, 28.3, false, 0xffffff, null, 'Neptune', 'The windiest planet with the strongest winds in the solar system. Distance from Sun: 30 AU (4.5 billion km)')
    ];
  }

  _createPlanet(radius, color, position, orbitRadius, orbitSpeed, rotationSpeed, tilt, hasRings, ringColor, texturePath, name, description) {
    const planetGroup = new THREE.Group();
    planetGroup.position.copy(position);

    const lod = new THREE.LOD();
    const highDetail = new THREE.SphereGeometry(radius, 32, 32);
    const lowDetail = new THREE.SphereGeometry(radius, 16, 16);
    const planetMaterial = new THREE.MeshPhongMaterial({
      color,
      shininess: 40,
      specular: 0x666666,
      emissive: new THREE.Color(color).multiplyScalar(0.3),
      emissiveIntensity: 0.4
    });
    const highDetailMesh = new THREE.Mesh(highDetail, planetMaterial);
    const lowDetailMesh = new THREE.Mesh(lowDetail, planetMaterial);
    highDetailMesh.castShadow = true;
    highDetailMesh.receiveShadow = true;
    lowDetailMesh.castShadow = true;
    lowDetailMesh.receiveShadow = true;
    lod.addLevel(highDetailMesh, 0);
    lod.addLevel(lowDetailMesh, 100);
    planetGroup.add(lod);

    planetGroup.rotation.x = tilt * Math.PI / 180;

    if (hasRings) {
      const ringGeometry = new THREE.RingGeometry(radius * 1.5, radius * 2.5, 64);
      const ringMaterial = new THREE.MeshPhongMaterial({
        color: ringColor,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.7,
        shininess: 30,
        specular: 0x333333,
        emissive: new THREE.Color(ringColor).multiplyScalar(0.1),
        emissiveIntensity: 0.2
      });
      const rings = new THREE.Mesh(ringGeometry, ringMaterial);
      rings.rotation.x = Math.PI / 2;
      rings.castShadow = false;
      rings.receiveShadow = true;
      lod.add(rings);
    }

    const orbitGeometry = new THREE.BufferGeometry();
    const orbitPoints = [];
    const segments = 128;
    for (let i = 0; i <= segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      orbitPoints.push(
        Math.cos(theta) * orbitRadius,
        0,
        Math.sin(theta) * orbitRadius
      );
    }
    orbitGeometry.setAttribute('position', new THREE.Float32BufferAttribute(orbitPoints, 3));
    const orbitMaterial = new THREE.LineBasicMaterial({ color: 0x666666, transparent: true, opacity: 0.3 });
    const orbit = new THREE.Line(orbitGeometry, orbitMaterial);
    this.scene.add(orbit);

    const planetShape = new CANNON.Sphere(radius);
    const planetBody = new CANNON.Body({
      mass: radius * 100,
      position: new CANNON.Vec3(position.x, position.y, position.z),
      shape: planetShape
    });
    this.world.addBody(planetBody);

    planetGroup.userData = { name, description, radius, type: 'planet' };
    return { mesh: lod, group: planetGroup, body: planetBody, orbit: { radius: orbitRadius, speed: orbitSpeed }, radius, name, description };
  }

  _createMoon(parentPlanet, radius, color, orbitRadius, orbitSpeed, name) {
    const moonGeometry = new THREE.SphereGeometry(radius, 16, 16);
    const moonMaterial = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.8,
      metalness: 0.1,
      emissive: color,
      emissiveIntensity: 0.05
    });
    const moonMesh = new THREE.Mesh(moonGeometry, moonMaterial);
    moonMesh.castShadow = false;
    moonMesh.receiveShadow = true;
    moonMesh.userData = { type: 'moon', name };

    const moonGroup = new THREE.Group();
    moonGroup.add(moonMesh);
    moonMesh.position.set(orbitRadius, 0, 0);
    parentPlanet.group.add(moonGroup);

    const moonBody = new CANNON.Body({
      mass: 0.1,
      position: new CANNON.Vec3(parentPlanet.group.position.x + orbitRadius, parentPlanet.group.position.y, parentPlanet.group.position.z)
    });
    moonBody.addShape(new CANNON.Sphere(radius));
    this.world.addBody(moonBody);

    const moonLight = new THREE.PointLight(0xffffff, 0.05, radius * 5);
    moonMesh.add(moonLight);

    moonGroup.userData = { name, description: `${name} - A moon orbiting ${parentPlanet.name}`, radius, type: 'moon' };
    return { mesh: moonMesh, group: moonGroup, body: moonBody, orbitSpeed: orbitSpeed * 0.1, orbitRadius, name, radius };
  }

  _createAsteroidBelt() {
    const asteroidBelt = new THREE.Group();
    const asteroidCount = 500;
    const minRadius = 480;
    const maxRadius = 650;
    const minSize = 0.1;
    const maxSize = 1.2;

    for (let i = 0; i < asteroidCount; i++) {
      const radius = Math.random() * (maxRadius - minRadius) + minRadius;
      const size = Math.random() * (maxSize - minSize) + minSize;
      const angle = Math.random() * Math.PI * 2;

      const asteroidGeometry = new THREE.IcosahedronGeometry(size, 0);
      const asteroidMaterial = new THREE.MeshStandardMaterial({
        color: 0x888888,
        roughness: 0.9,
        metalness: 0.1
      });
      const asteroid = new THREE.Mesh(asteroidGeometry, asteroidMaterial);
      asteroid.position.set(
        Math.cos(angle) * radius,
        (Math.random() - 0.5) * 2,
        Math.sin(angle) * radius
      );
      asteroid.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      asteroid.castShadow = false;
      asteroid.receiveShadow = true;
      asteroid.userData = {
        orbitRadius: radius,
        orbitSpeed: (0.0005 + Math.random() * 0.0005) * 1.3,
        rotationSpeed: { x: Math.random() * 0.01, y: Math.random() * 0.01, z: Math.random() * 0.01 }
      };
      asteroidBelt.add(asteroid);
    }

    return asteroidBelt;
  }

  _createUFO() {
    const ufoGroup = new THREE.Group();
    const bodyGeometry = new THREE.CapsuleGeometry(2, 1, 16);
    const bodyMaterial = new THREE.MeshPhongMaterial({ color: 0x666666, metalness: 0.8 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.rotation.z = Math.PI / 2;
    body.castShadow = false;
    ufoGroup.add(body);

    const cockpitGeometry = new THREE.SphereGeometry(1.2, 16, 8);
    const cockpitMaterial = new THREE.MeshPhongMaterial({ color: 0x44ff44, transparent: true, opacity: 0.6 });
    const cockpit = new THREE.Mesh(cockpitGeometry, cockpitMaterial);
    cockpit.position.y = 0.5;
    ufoGroup.add(cockpit);

    for (let i = 0; i < 8; i++) {
      const light = new THREE.PointLight(0x44ff44, 0.5, 3);
      light.position.set(
        Math.cos(i / 8 * Math.PI * 2) * 2,
        -0.5,
        Math.sin(i / 8 * Math.PI * 2) * 2
      );
      ufoGroup.add(light);
    }

    ufoGroup.position.set(0, 4, 0);
    return ufoGroup;
  }

  _createSatellite() {
    const satelliteGroup = new THREE.Group();
    const bodyGeometry = new THREE.BoxGeometry(1, 1, 2);
    const bodyMaterial = new THREE.MeshPhongMaterial({ color: 0xdddddd, shininess: 80, specular: 0x666666 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.castShadow = false;
    satelliteGroup.add(body);

    const panelGeometry = new THREE.BoxGeometry(4, 0.1, 1);
    const panelMaterial = new THREE.MeshPhongMaterial({ color: 0x2266aa, shininess: 100, specular: 0x6688cc });
    const panel = new THREE.Mesh(panelGeometry, panelMaterial);
    satelliteGroup.add(panel);

    const antennaGeometry = new THREE.CylinderGeometry(0.05, 0.05, 1.5, 8);
    const antennaMaterial = new THREE.MeshPhongMaterial({ color: 0x888888 });
    const antenna = new THREE.Mesh(antennaGeometry, antennaMaterial);
    antenna.position.set(0, 0, 1.5);
    antenna.rotation.x = Math.PI / 2;
    satelliteGroup.add(antenna);

    const planetIndex = Math.floor(Math.random() * this.planets.length);
    const planet = this.planets[planetIndex];
    const planetPos = planet ? planet.group.position.clone() : new THREE.Vector3(0, 0, 0);
    const orbitRadius = planet ? planet.radius * 3 : 20;
    const orbitAngle = Math.random() * Math.PI * 2;
    satelliteGroup.position.set(
      planetPos.x + Math.cos(orbitAngle) * orbitRadius,
      (Math.random() - 0.5) * 10,
      planetPos.z + Math.sin(orbitAngle) * orbitRadius
    );

    const satelliteShape = new CANNON.Box(new CANNON.Vec3(0.5, 0.5, 1));
    const satelliteBody = new CANNON.Body({
      mass: 0.05,
      position: new CANNON.Vec3(satelliteGroup.position.x, satelliteGroup.position.y, satelliteGroup.position.z),
      shape: satelliteShape
    });
    this.world.addBody(satelliteBody);

    return {
      group: satelliteGroup,
      body: satelliteBody,
      orbit: { target: planet, radius: orbitRadius, height: satelliteGroup.position.y, speed: 0.0001, angle: orbitAngle },
      name: 'Satellite',
      description: 'An artificial satellite orbiting in space'
    };
  }

  _createComet() {
    const cometGroup = new THREE.Group();
    const nucleusGeometry = new THREE.IcosahedronGeometry(0.5, 0);
    const nucleusMaterial = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.9, metalness: 0.1 });
    const nucleus = new THREE.Mesh(nucleusGeometry, nucleusMaterial);
    nucleus.castShadow = false;
    nucleus.receiveShadow = true;
    cometGroup.add(nucleus);

    const tailGeometry = new THREE.BufferGeometry();
    const tailPositions = [];
    const tailColors = [];
    const particleCount = 300;
    for (let i = 0; i < particleCount; i++) {
      const length = Math.random() * 10 + 2;
      const angle = Math.random() * Math.PI * 0.1 + Math.PI * 0.95;
      const spread = Math.random() * 0.5;
      tailPositions.push(
        Math.cos(angle) * length + (Math.random() - 0.5) * spread,
        (Math.random() - 0.5) * spread,
        Math.sin(angle) * length + (Math.random() - 0.5) * spread
      );
      const color = new THREE.Color(0xccccff);
      tailColors.push(color.r, color.g, color.b);
    }
    tailGeometry.setAttribute('position', new THREE.Float32BufferAttribute(tailPositions, 3));
    tailGeometry.setAttribute('color', new THREE.Float32BufferAttribute(tailColors, 3));
    const tailMaterial = new THREE.PointsMaterial({
      size: 0.1,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.5,
      vertexColors: true
    });
    const tail = new THREE.Points(tailGeometry, tailMaterial);
    cometGroup.add(tail);

    cometGroup.position.set(250, 50, 0);
    const cometShape = new CANNON.Sphere(0.5);
    const cometBody = new CANNON.Body({
      mass: 1,
      position: new CANNON.Vec3(250, 50, 0),
      shape: cometShape
    });
    this.world.addBody(cometBody);

    return { group: cometGroup, body: cometBody, orbit: { radius: 250, height: 50, speed: 0.001 }, name: 'Comet', description: 'An icy celestial object with a visible tail' };
  }

  _createRocket() {
    const rocketGroup = new THREE.Group();
    const bodyGeometry = new THREE.CylinderGeometry(1, 1, 7, 16);
    const bodyMaterial = new THREE.MeshPhongMaterial({ color: 0xdddddd, shininess: 90, specular: 0x333333 });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    body.rotation.x = Math.PI / 2;
    body.castShadow = false;
    body.receiveShadow = true;
    rocketGroup.add(body);

    const noseGeometry = new THREE.ConeGeometry(1, 3, 16);
    const noseMaterial = new THREE.MeshPhongMaterial({ color: 0xff3333, shininess: 80, specular: 0x333333 });
    const nose = new THREE.Mesh(noseGeometry, noseMaterial);
    nose.position.set(0, 0, 5);
    nose.rotation.x = Math.PI / 2;
    nose.castShadow = false;
    nose.receiveShadow = true;
    rocketGroup.add(nose);

    const finShape = new THREE.Shape();
    finShape.moveTo(0, 0);
    finShape.lineTo(3, 0);
    finShape.lineTo(0, 2);
    finShape.lineTo(0, 0);
    const finGeometry = new THREE.ShapeGeometry(finShape);
    const finMaterial = new THREE.MeshPhongMaterial({ color: 0x3333ff, shininess: 80, specular: 0x333333 });
    for (let i = 0; i < 3; i++) {
      const fin = new THREE.Mesh(finGeometry, finMaterial);
      const angle = (i * Math.PI * 2) / 3;
      fin.position.set(0, 0, -2.5);
      fin.rotation.z = angle;
      fin.rotation.y = Math.PI / 2;
      fin.castShadow = false;
      fin.receiveShadow = true;
      rocketGroup.add(fin);
    }

    const engineGeometry = new THREE.CircleGeometry(0.8, 16);
    const engineMaterial = new THREE.MeshPhongMaterial({
      color: 0xff9933,
      emissive: 0xff9933,
      emissiveIntensity: 1,
      side: THREE.DoubleSide
    });
    const engine = new THREE.Mesh(engineGeometry, engineMaterial);
    engine.position.set(0, 0, -3.6);
    engine.rotation.y = Math.PI;
    rocketGroup.add(engine);

    rocketGroup.position.set(170, 0, -120);
    rocketGroup.name = 'Rocket';
    rocketGroup.userData = { description: 'A space rocket exploring the solar system. Speed: 28,000 km/h, Length: 50m, Thrust: 3.5 million pounds.' };
    return rocketGroup;
  }

  _createSpaceStation() {
    const stationGroup = new THREE.Group();
    const baseGeometry = new THREE.CylinderGeometry(0.3, 0.3, 0.8, 16);
    const baseMaterial = new THREE.MeshPhongMaterial({ color: 0xdddddd, shininess: 90, specular: 0x333333 });
    const baseModule = new THREE.Mesh(baseGeometry, baseMaterial);
    baseModule.rotation.x = Math.PI / 2;
    baseModule.castShadow = false;
    baseModule.receiveShadow = true;
    stationGroup.add(baseModule);

    const panelGeometry = new THREE.BoxGeometry(15, 0.2, 5);
    const panelMaterial = new THREE.MeshPhongMaterial({
      color: 0x2266aa,
      shininess: 100,
      specular: 0x6688cc,
      emissive: 0x001133,
      emissiveIntensity: 0.5
    });
    const leftPanel = new THREE.Mesh(panelGeometry, panelMaterial);
    leftPanel.position.set(-9, 0, 0);
    stationGroup.add(leftPanel);
    const rightPanel = new THREE.Mesh(panelGeometry, panelMaterial);
    rightPanel.position.set(9, 0, 0);
    stationGroup.add(rightPanel);

    const lightGeometry = new THREE.SphereGeometry(0.3, 8, 8);
    const redLightMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const greenLightMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const redLight = new THREE.Mesh(lightGeometry, redLightMaterial);
    redLight.position.set(0, 0, 4.5);
    stationGroup.add(redLight);
    const greenLight = new THREE.Mesh(lightGeometry, greenLightMaterial);
    greenLight.position.set(0, 0, -4.5);
    stationGroup.add(greenLight);

    const earth = this.planets[2];
    const earthPos = earth ? earth.group.position.clone() : new THREE.Vector3(50, 0, 0);
    stationGroup.position.set(earthPos.x + 8, 4, earthPos.z + 8);

    const stationShape = new CANNON.Box(new CANNON.Vec3(3, 3, 4));
    const stationBody = new CANNON.Body({
      mass: 0.1,
      position: new CANNON.Vec3(stationGroup.position.x, stationGroup.position.y, stationGroup.position.z),
      shape: stationShape
    });
    this.world.addBody(stationBody);

    stationGroup.userData = { name: 'International Space Station', description: 'Orbiting laboratory in low Earth orbit', type: 'station' };
    return {
      group: stationGroup,
      body: stationBody,
      redLight,
      greenLight,
      orbit: { target: earth, radius: 8, height: 4, speed: 0.00005, angle: 0 },
      name: 'International Space Station',
      description: 'Orbiting laboratory in low Earth orbit'
    };
  }

  _createInfoCardSystem() {
    const infoCardContainer = document.createElement('div');
    infoCardContainer.id = 'info-card-container';
    Object.assign(infoCardContainer.style, {
      position: 'absolute',
      left: '50%',
      bottom: '10%',
      transform: 'translateX(-50%)',
      padding: '20px',
      width: 'auto',
      minWidth: '300px',
      maxWidth: '500px',
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(8px)',
      color: 'white',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      fontFamily: 'system-ui, Arial, sans-serif',
      textAlign: 'center',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
      zIndex: '1000',
      opacity: '0',
      transition: 'all 0.5s',
      pointerEvents: 'none'
    });

    const titleElement = document.createElement('h2');
    titleElement.id = 'info-card-title';
    Object.assign(titleElement.style, {
      margin: '0 0 10px 0',
      fontSize: '1.5em',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(0, 150, 255, 0.5)',
      paddingBottom: '5px'
    });
    infoCardContainer.appendChild(titleElement);

    const descriptionElement = document.createElement('p');
    descriptionElement.id = 'info-card-description';
    Object.assign(descriptionElement.style, {
      margin: '0',
      fontSize: '1.1em',
      lineHeight: '1.4'
    });
    infoCardContainer.appendChild(descriptionElement);

    const scienceDataElement = document.createElement('div');
    scienceDataElement.id = 'info-card-science';
    Object.assign(scienceDataElement.style, {
      marginTop: '10px',
      fontSize: '0.9em',
      fontStyle: 'italic',
      borderTop: '1px solid rgba(255,255,255,0.3)',
      paddingTop: '8px'
    });
    infoCardContainer.appendChild(scienceDataElement);

    document.body.appendChild(infoCardContainer);

    return {
      container: infoCardContainer,
      titleEl: titleElement,
      descriptionEl: descriptionElement,
      scienceDataEl: scienceDataElement,
      currentObject: null,
      isVisible: false,
      show: function(title, description, scienceData) {
        const isNewObject = !this.currentObject || this.currentObject !== title;
        this.currentObject = title;
        if (isNewObject) {
          this.container.style.transform = 'translateX(-50%) translateY(20px) scale(0.95)';
          this.container.style.opacity = '0';
        }
        this.titleEl.innerHTML = title ? `<span style="background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">${title}</span>` : 'Unknown Object';
        this.descriptionEl.innerHTML = description ? description.replace(/\. /g, '.<br>') : 'No description available.';
        this.scienceDataEl.innerHTML = scienceData ? `<div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2);">🔬 <i>Science Data:</i></div><div style="font-size: 0.9em; opacity: 0.9; margin-top: 5px;">${scienceData}</div>` : '';
        setTimeout(() => {
          this.container.style.opacity = '1';
          this.container.style.transform = 'translateX(-50%) translateY(0) scale(1)';
          this.isVisible = true;
        }, isNewObject ? 50 : 0);
      },
      hide: function() {
        if (this.isVisible) {
          this.container.style.opacity = '0';
          this.container.style.transform = 'translateX(-50%) translateY(10px) scale(0.95)';
          this.isVisible = false;
        }
      }
    };
  }

  async _loadAssets() {
    return new Promise((resolve) => setTimeout(resolve, 1000));
  }

  async _initMenu() {
    if (this.menuManager && typeof this.menuManager.init === 'function') {
      await this.menuManager.init();
      if (typeof window !== 'undefined') {
        window.menuManager = this.menuManager;
      }
    }
  }

  _hideLoadingScreen() {
    const loadingScreen = document.querySelector('.loading-screen');
    if (loadingScreen) {
      loadingScreen.style.opacity = '0';
      setTimeout(() => {
        loadingScreen.style.display = 'none';
      }, 500);
    }
  }

  startAnimationLoop() {
    if (appState.animationLoopActive) {
      console.warn('Animation loop already running');
      return;
    }
    appState.animationLoopActive = true;
    this.isAnimating = true;
    this.animate();
  }

  stopAnimationLoop() {
    appState.animationLoopActive = false;
    this.isAnimating = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  animate() {
    if (!appState.animationLoopActive) {
      return;
    }
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

    try {
      if (this.stats) {
        this.stats.update();
      }
      const delta = this.clock.getDelta();

      if (this.controls) this.controls.update();
      TWEEN.update();
      if (this.world) this.world.step(1/60, delta, 3);

      if (!this.spaceStation) {
        this.spaceStation = this._createSpaceStation();
        if (this.spaceStation && this.spaceStation.group) {
          this.scene.add(this.spaceStation.group);
          this.spaceObjects.push(this.spaceStation);
        }
      }

      animateSpaceObjects(
        {
          planets: this.planets,
          moons: this.moons,
          spaceStation: this.spaceStation,
          rocket: this.rocket,
          ufo: this.ufo,
          comet: this.comet,
          satellites: this.satellites,
          asteroidBelt: this.asteroidBelt
        },
        this.clock,
        this.camera,
        this.scene,
        this._updateInfoCards.bind(this)
      );

      if (appState.targetPlanetToFollow) {
        this.controls.target.copy(appState.targetPlanetToFollow.group.position);
        const currentDistance = this.camera.position.distanceTo(this.controls.target);
        const cameraIdealPosition = new THREE.Vector3()
          .subVectors(this.camera.position, this.controls.target)
          .normalize()
          .multiplyScalar(currentDistance)
          .add(this.controls.target);
        this.camera.position.lerp(cameraIdealPosition, 0.08);
        this.controls.update();
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    } catch (error) {
      console.error('Error in animation loop:', error);
    }
  }

  _updateInfoCards() {
    try {
      const cameraPosition = this.camera.position.clone();
      let closestObject = null;
      let closestDistance = Infinity;
      let minDistanceThreshold = appState.currentFocusTarget ? 40 : 25;

      if (this.controls) {
        const cameraVelocity = cameraPosition.distanceTo(appState.previousCameraPosition || cameraPosition);
        const speedFactor = Math.min(2, Math.max(1, 1 + (cameraVelocity * 10)));
        minDistanceThreshold *= speedFactor;
        appState.previousCameraPosition = cameraPosition.clone();
      }

      this.planets.forEach(planet => {
        if (planet && planet.group && planet.group.position) {
          const distance = cameraPosition.distanceTo(planet.group.position);
          if (distance < minDistanceThreshold && distance < closestDistance) {
            closestObject = planet;
            closestDistance = distance;
          }
        }
      });

      const sunDistance = cameraPosition.distanceTo(new THREE.Vector3(0, 0, 0));
      if (sunDistance < minDistanceThreshold && sunDistance < closestDistance) {
        closestObject = {
          name: 'The Sun',
          description: 'The star at the center of our solar system',
          userData: { scienceData: 'Diameter: 1,392,700 km, Surface Temperature: 5,500°C, Age: 4.6 billion years, Type: G-type main-sequence star (G2V)' }
        };
        closestDistance = sunDistance;
      }

      const checkSpaceObject = (obj, name, description, scienceData) => {
        if (obj && obj.group && obj.group.position) {
          const distance = cameraPosition.distanceTo(obj.group.position);
          if (distance < minDistanceThreshold && distance < closestDistance) {
            closestObject = { name: name || obj.name, description: description || obj.description, userData: { scienceData: scienceData || '' } };
            closestDistance = distance;
          }
        }
      };

      checkSpaceObject(this.ufo, 'UFO', 'An unknown spacecraft observing the solar system', 'Origin: Unknown, Technology: Advanced propulsion system, Purpose: Observational');
      checkSpaceObject(this.spaceStation, 'Space Station', 'International space station orbiting Earth', 'Orbit: Low Earth Orbit, Mass: 420,000 kg, Crew Capacity: 6-7 astronauts');
      checkSpaceObject(this.rocket, 'Rocket', 'A spacecraft exploring the solar system', 'Speed: 28,000 km/h, Length: 50m, Thrust: 3.5 million pounds');
      checkSpaceObject(this.comet, 'Comet', 'An icy celestial object with a visible tail', 'Composition: Ice, dust, and rocky particles, Orbital Period: Variable, Origin: Kuiper Belt/Oort Cloud');
      this.satellites.forEach((satellite, index) => {
        checkSpaceObject(satellite, `Satellite ${index+1}`, 'An artificial satellite orbiting in space', 'Orbit: Various Earth orbits, Purpose: Communications/Navigation/Science, Power: Solar arrays');
      });

      if (closestObject) {
        const distanceRatio = closestDistance / minDistanceThreshold;
        const opacityFactor = Math.max(0, Math.min(1, 1 - Math.pow(distanceRatio, 2)));
        if (opacityFactor > 0.1 && this.infoCard) {
          const scienceData = closestObject.userData && closestObject.userData.scienceData
            ? closestObject.userData.scienceData
            : '';
          this.infoCard.show(closestObject.name, closestObject.description, scienceData);
          this.infoCard.container.style.opacity = opacityFactor.toString();
        } else if (this.infoCard) {
          this.infoCard.hide();
        }
      } else if (this.infoCard) {
        this.infoCard.hide();
      }
    } catch (error) {
      console.error('Error updating info cards:', error);
      if (this.infoCard) {
        this.infoCard.hide();
      }
    }
  }

  _resetCameraView() {
    if (this.camera && this.controls) {
      const initialPos = CONFIG.camera.position;
      new TWEEN.Tween(this.camera.position)
        .to({ x: initialPos.x, y: initialPos.y, z: initialPos.z }, 1000)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .start();
      new TWEEN.Tween(this.controls.target)
        .to({ x: 0, y: 0, z: 0 }, 1000)
        .easing(TWEEN.Easing.Quadratic.InOut)
        .onUpdate(() => this.controls.update())
        .start();
      appState.currentFocusTarget = null;
      appState.targetPlanetToFollow = null;
      appState.currentPlanetIndex = -1;
      if (this.infoCard) {
        this.infoCard.hide();
      }
      showNotification('Camera reset to initial view', 'info', 3000);
    }
  }

  handleResize() {
    if (this.camera && this.renderer) {
      const width = window.innerWidth;
      const height = window.innerHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    }
  }

  cleanup() {
    try {
      this.stopAnimationLoop();

      if (this.renderer) {
        this.renderer.dispose();
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
        }
      }

      if (this.scene) {
        this.scene.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(mat => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }

      if (this.world) {
        while (this.world.bodies.length > 0) {
          this.world.removeBody(this.world.bodies[0]);
        }
      }

      if (this.infoCard && this.infoCard.container && this.infoCard.container.parentNode) {
        this.infoCard.container.parentNode.removeChild(this.infoCard.container);
      }

      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('beforeunload', this.cleanup);
      this.renderer.domElement.removeEventListener('click', this.handleClick);

      if (this.audioManager && typeof this.audioManager.cleanup === 'function') {
        this.audioManager.cleanup();
      }
      if (this.menuManager && typeof this.menuManager.cleanup === 'function') {
        this.menuManager.cleanup();
      }
      if (this.navigationManager && typeof this.navigationManager.cleanup === 'function') {
        this.navigationManager.cleanup();
      }
      if (this.touchControls && typeof this.touchControls.cleanup === 'function') {
        this.touchControls.cleanup();
      }
      if (this.keyboardNavigation && typeof this.keyboardNavigation.cleanup === 'function') {
        this.keyboardNavigation.cleanup();
      }

      if (this.stats && this.stats.dom && this.stats.dom.parentNode) {
        this.stats.dom.parentNode.removeChild(this.stats.dom);
      }

      console.log('App cleaned up successfully');
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  _setupMenuItems() {
    // Tambahkan item menu untuk kategori 'Planets & Sun'
    if (this.planets && this.planets.length > 0) {
        this.planets.forEach(planet => {
            menuManager.addMenuItem({
                label: planet.name, // Gunakan nama planet sebagai label
                categoryId: 'planets-sun', // Sesuaikan dengan ID kategori di MenuManager
                onClick: () => {
                    // Aksi saat item menu planet diklik: fokus pada planet
                    this.navigationManager.focusOnObject(planet.group);
                },
                // Tambahkan ikon jika tersedia (opsional)
                // icon: planet.icon // Asumsi objek planet memiliki properti icon
            });
        });
    }

    if (this.sun && this.sun.group) {
         menuManager.addMenuItem({
            label: this.sun.name || 'Sun', // Gunakan nama matahari sebagai label
            categoryId: 'planets-sun', // Sesuaikan dengan ID kategori di MenuManager
            onClick: () => {
                // Aksi saat item menu matahari diklik: fokus pada matahari
                this.navigationManager.focusOnObject(this.sun);
            },
            // Tambahkan ikon jika tersedia (opsional)
            // icon: '☀️'
        });
    }

    // Tambahkan item menu untuk kategori 'Space Objects'
    if (this.moons && this.moons.length > 0) {
         this.moons.forEach(moon => {
            menuManager.addMenuItem({
                label: moon.name, // Gunakan nama bulan sebagai label
                categoryId: 'space-objects', // Sesuaikan dengan ID kategori
                onClick: () => {
                    // Aksi saat item menu bulan diklik: fokus pada bulan
                     this.navigationManager.focusOnObject(moon.group);
                },
                 // Tambahkan ikon jika tersedia (opsional)
                // icon: '🌙'
            });
        });
    }
    
    if (this.asteroidBelt && this.asteroidBelt.group) {
         menuManager.addMenuItem({
            label: this.asteroidBelt.name || 'Asteroid Belt', // Gunakan nama sebagai label
            categoryId: 'space-objects', // Sesuaikan dengan ID kategori
            onClick: () => {
                // Aksi saat item menu diklik: fokus
                this.navigationManager.focusOnObject(this.asteroidBelt);
            },
             // Tambahkan ikon jika tersedia (opsional)
            // icon: '💫'
        });
    }

    if (this.ufo && this.ufo.group) {
         menuManager.addMenuItem({
            label: this.ufo.name || 'UFO', // Gunakan nama sebagai label
            categoryId: 'space-objects', // Sesuaikan dengan ID kategori
            onClick: () => {
                // Aksi saat item menu diklik: fokus
                this.navigationManager.focusOnObject(this.ufo);
            },
             // Tambahkan ikon jika tersedia (opsional)
            // icon: '🛸'
        });
    }

     if (this.spaceStation && this.spaceStation.group) {
         menuManager.addMenuItem({
            label: this.spaceStation.name || 'Space Station', // Gunakan nama sebagai label
            categoryId: 'space-objects', // Sesuaikan dengan ID kategori
            onClick: () => {
                // Aksi saat item menu diklik: fokus
                this.navigationManager.focusOnObject(this.spaceStation.group);
            },
             // Tambahkan ikon jika tersedia (opsional)
            // icon: '🛰️'
        });
    }

     if (this.rocket && this.rocket.group) {
         menuManager.addMenuItem({
            label: this.rocket.name || 'Rocket', // Gunakan nama sebagai label
            categoryId: 'space-objects', // Sesuaikan dengan ID kategori
            onClick: () => {
                // Aksi saat item menu diklik: fokus
                this.navigationManager.focusOnObject(this.rocket);
            },
             // Tambahkan ikon jika tersedia (opsional)
            // icon: '🚀'
        });
    }

    if (this.comet && this.comet.group) {
         menuManager.addMenuItem({
            label: this.comet.name || 'Comet', // Gunakan nama sebagai label
            categoryId: 'space-objects', // Sesuaikan dengan ID kategori
            onClick: () => {
                // Aksi saat item menu diklik: fokus
                this.navigationManager.focusOnObject(this.comet.group);
            },
             // Tambahkan ikon jika tersedia (opsional)
            // icon: '☄️'
        });
    }

    if (this.satellites && this.satellites.length > 0) {
         this.satellites.forEach((satellite, index) => {
            menuManager.addMenuItem({
                label: `${satellite.name || 'Satellite'} ${index + 1}`, // Gunakan nama + index
                categoryId: 'space-objects', // Sesuaikan dengan ID kategori
                onClick: () => {
                    // Aksi saat item menu diklik: fokus
                    this.navigationManager.focusOnObject(satellite.group);
                },
                 // Tambahkan ikon jika tersedia (opsional)
                // icon: '📡'
            });
        });
    }

    // Tambahkan item menu untuk kategori 'Controls & Navigation' (opsional, mungkin hanya info)
    // Jika ada aksi navigasi spesifik yang ingin diletakkan di menu, tambahkan di sini.
    // Contoh:
    // menuManager.addMenuItem({
    //     label: 'Reset Camera',
    //     categoryId: 'controls-nav',
    //     onClick: () => {
    //         this.navigationManager.handleNavigationAction('reset-camera');
    //     }
    // });
  }
}

const app = new App();
document.addEventListener('DOMContentLoaded', () => app.init());
export default app;